/**
 * Cloudflare Turnstile helper（注册/发码防刷）。
 * 未配置 VUE_APP_TURNSTILE_SITE_KEY 时返回空 token（后端 turnstile_enabled=false 时放行）。
 * Managed Widget：render 后自动出 token；勿强制 size=invisible / execution=execute（易空 token）。
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
let hostEl = null;
let widgetId = null;

export function getTurnstileSiteKey() {
  return String(process.env.VUE_APP_TURNSTILE_SITE_KEY || '').trim();
}

export function isTurnstileConfigured() {
  return Boolean(getTurnstileSiteKey());
}

function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-deepsage-turnstile]');
    if (existing) {
      const done = () => {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error('turnstile script loaded but api missing'));
      };
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }
      existing.addEventListener('load', done);
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.dataset.deepsageTurnstile = '1';
    s.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('turnstile script loaded but api missing'));
    };
    s.onerror = () => reject(new Error('turnstile script failed'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

function ensureHost() {
  if (hostEl && document.body.contains(hostEl)) return hostEl;
  hostEl = document.createElement('div');
  hostEl.id = 'deepsage-turnstile-host';
  hostEl.setAttribute('aria-hidden', 'true');
  hostEl.style.cssText =
    'position:fixed;left:50%;bottom:16px;transform:translateX(-50%);z-index:2147483000;';
  document.body.appendChild(hostEl);
  return hostEl;
}

function resetHostStyle() {
  if (!hostEl) return;
  hostEl.style.left = '50%';
  hostEl.style.bottom = '16px';
  hostEl.style.transform = 'translateX(-50%)';
  hostEl.style.opacity = '1';
  hostEl.style.pointerEvents = 'auto';
}

function hideHost() {
  if (!hostEl) return;
  hostEl.style.left = '-9999px';
  hostEl.style.bottom = '0';
  hostEl.style.transform = '';
  hostEl.style.opacity = '0';
  hostEl.style.pointerEvents = 'none';
}

/**
 * @param {string} [action]
 * @returns {Promise<string>}
 */
export async function requestTurnstileToken(action = 'auth') {
  const siteKey = getTurnstileSiteKey();
  if (!siteKey) return '';

  const turnstile = await loadTurnstileScript();
  if (!turnstile?.render) {
    throw new Error('turnstile api unavailable');
  }

  const host = ensureHost();
  resetHostStyle();
  if (widgetId != null && turnstile.remove) {
    try {
      turnstile.remove(widgetId);
    } catch {
      /* ignore */
    }
    widgetId = null;
  }
  host.innerHTML = '';

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn) => (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      hideHost();
      fn(value);
    };

    const timer = setTimeout(() => {
      finish(reject)(new Error('turnstile timeout'));
    }, 25000);

    try {
      widgetId = turnstile.render(host, {
        sitekey: siteKey,
        theme: 'auto',
        retry: 'auto',
        'refresh-expired': 'auto',
        callback: (token) => finish(resolve)(String(token || '').trim()),
        'error-callback': () => finish(reject)(new Error('turnstile error')),
        'expired-callback': () => finish(reject)(new Error('turnstile expired')),
        'timeout-callback': () => finish(reject)(new Error('turnstile user timeout')),
        // action 仅作遥测；过长会被拒
        action: String(action || 'auth').slice(0, 32),
      });
    } catch (err) {
      finish(reject)(err);
    }
  });
}
